import { useState } from 'react'
import { isInAppBrowser, externalUrlFor, escapeHint } from '../../utils/inAppBrowser'

/**
 * The one thing worth saying to somebody who arrived from a Messenger thread.
 *
 * They are in Facebook's browser, not one they chose, and **Google refuses OAuth inside an
 * embedded webview** — so signing in cannot be made to work there by anything on our side.
 * Until now the app let them find that out by failing: they filled in a form, pressed the
 * button, and met a sentence in `auth.js` explaining that the last two minutes were never
 * going to work.
 *
 * So it is said first, once, in a strip at the top.
 *
 * ## Why it is dismissible
 *
 * Half of what arrives from a Messenger link needs no account at all. The public tracking
 * page and the price checker both work perfectly inside the webview, and nagging somebody
 * watching a rider approach their house is noise. Dismissing lasts the session — long
 * enough not to hector, short enough that the next visit says it again, because the next
 * visit may be the one where they try to order.
 *
 * ## Why the button is not always there
 *
 * Android can eject a page to Chrome with an `intent://` URL. iOS cannot — Apple never
 * shipped a supported way for a page to leave a webview — so on iPhone the strip carries
 * the instruction and the copy button instead of a control that would do nothing. A button
 * that silently fails is worse than no button; the person presses it, nothing happens, and
 * now they distrust the screen as well as being stuck.
 */

const DISMISSED = 'machi.inapp.dismissed'

const wasDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISSED) === '1'
  } catch {
    // Private mode, storage off. Showing it is the safe direction.
    return false
  }
}

export default function OpenInBrowser() {
  const [hidden, setHidden] = useState(() => !isInAppBrowser() || wasDismissed())
  const [copied, setCopied] = useState(false)

  if (hidden) return null

  const href = globalThis.location?.href ?? ''
  const escape = externalUrlFor(href)

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISSED, '1')
    } catch {
      // Nothing to do; it simply reappears on the next screen, which is not harmful.
    }

    setHidden(true)
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(href)
      setCopied(true)
    } catch {
      // No clipboard permission in this webview. The instruction below still stands, and
      // it is the one that always works.
      setCopied(false)
    }
  }

  return (
    <aside
      role="region"
      aria-label="Buksan sa ibang browser"
      className="border-b border-hairline bg-blush px-5 py-3.5"
    >
      <div className="mx-auto w-full max-w-[26rem]">
        <p className="text-body font-semibold text-ink">
          Buksan po ito sa totoong browser
        </p>

        <p className="mt-1 text-body-s text-ink-2">
          Hindi gumagana ang pag-sign in dito sa loob ng Facebook at Messenger. Ang
          pagtingin ng presyo at pagsubaybay ng order ay pwede pa rin dito.
        </p>

        <div className="flex flex-wrap gap-2.5 mt-3">
          {escape ? (
            <a
              href={escape}
              className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-btn
                         bg-brand text-on-brand text-body font-semibold"
            >
              Buksan sa Chrome
            </a>
          ) : (
            <button
              type="button"
              onClick={copy}
              className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-btn
                         bg-brand text-on-brand text-body font-semibold"
            >
              {copied ? 'Nakopya na ✓' : 'Kopyahin ang link'}
            </button>
          )}

          <button
            type="button"
            onClick={dismiss}
            className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-btn
                       border border-edge text-body font-semibold text-ink"
          >
            Dito na lang
          </button>
        </div>

        {/* Always shown, even beside the Chrome button. The intent link is refused by some
            builds of the Facebook app, and when it is, this sentence is the whole answer. */}
        <p className="mt-2.5 text-label text-ink-3">{escapeHint()}</p>
      </div>
    </aside>
  )
}
